import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Server, 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2, 
  RefreshCw,
  CheckCircle,
  XCircle,
  Users,
} from 'lucide-react';
import { useWppServices, WppService } from '@/hooks/useWppServices';
import { cn } from '@/lib/utils';

export function WhatsAppServersAdmin() {
  const {
    services,
    servicesLoading,
    updateServer,
    createServer,
    deleteServer,
    isUpdating,
    isCreating,
    isDeleting,
    refetch,
  } = useWppServices();

  const [editingServer, setEditingServer] = useState<WppService | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Form state para criar/editar
  const [formData, setFormData] = useState({
    nome: '',
    url: '',
    api_token: '',
    max_sessions: 3,
    is_shared: false,
    region: 'south-america',
  });

  const resetForm = () => {
    setFormData({
      nome: '',
      url: '',
      api_token: '',
      max_sessions: 3,
      is_shared: false,
      region: 'south-america',
    });
  };

  const handleEdit = (server: WppService) => {
    setEditingServer(server);
    setFormData({
      nome: server.nome,
      url: server.url,
      api_token: server.api_token,
      max_sessions: server.max_sessions ?? 3,
      is_shared: server.is_shared ?? false,
      region: server.region ?? 'south-america',
    });
  };

  const handleSaveEdit = () => {
    if (!editingServer) return;
    
    updateServer({
      id: editingServer.id,
      updates: {
        url: formData.url,
        api_token: formData.api_token,
        max_sessions: formData.max_sessions,
        region: formData.region,
      },
    });
    setEditingServer(null);
    resetForm();
  };

  const handleCreate = () => {
    createServer({
      nome: formData.nome,
      url: formData.url,
      api_token: formData.api_token,
      max_sessions: formData.max_sessions,
      is_shared: formData.is_shared,
      region: formData.region,
    });
    setIsCreateDialogOpen(false);
    resetForm();
  };

  const handleDelete = () => {
    if (!deleteConfirmId) return;
    deleteServer(deleteConfirmId);
    setDeleteConfirmId(null);
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-500">Ativo</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inativo</Badge>;
      case 'maintenance':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-600">Manutenção</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (servicesLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                Servidores WPP
              </CardTitle>
              <CardDescription>
                Gerencie os servidores WPPConnect/Evolution API
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Servidor
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Sessões</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Região</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Nenhum servidor configurado
                  </TableCell>
                </TableRow>
              ) : (
                services.map((server) => (
                  <TableRow key={server.id}>
                    <TableCell className="font-medium">{server.nome}</TableCell>
                    <TableCell>
                      {server.is_shared ? (
                        <Badge variant="outline" className="gap-1">
                          <Users className="h-3 w-3" />
                          Compartilhado
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Individual</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        "font-medium",
                        (server.current_sessions ?? 0) >= (server.max_sessions ?? 3) && "text-destructive"
                      )}>
                        {server.current_sessions ?? 0}
                      </span>
                      <span className="text-muted-foreground">/{server.max_sessions ?? 3}</span>
                    </TableCell>
                    <TableCell>{getStatusBadge(server.status)}</TableCell>
                    <TableCell className="text-muted-foreground">{server.region ?? '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(server)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteConfirmId(server.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog de Edição */}
      <Dialog open={!!editingServer} onOpenChange={() => setEditingServer(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Servidor</DialogTitle>
            <DialogDescription>
              Atualize as configurações do servidor {editingServer?.nome}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-url">URL do Servidor</Label>
              <Input
                id="edit-url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://wpp-service.up.railway.app"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-token">API Token</Label>
              <Input
                id="edit-token"
                type="password"
                value={formData.api_token}
                onChange={(e) => setFormData({ ...formData, api_token: e.target.value })}
                placeholder="Token de autenticação"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-max">Máx. Sessões</Label>
              <Input
                id="edit-max"
                type="number"
                min={1}
                max={10}
                value={formData.max_sessions}
                onChange={(e) => setFormData({ ...formData, max_sessions: parseInt(e.target.value) || 3 })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-region">Região</Label>
              <Input
                id="edit-region"
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                placeholder="south-america"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingServer(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={isUpdating}>
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Criação */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Servidor WPP</DialogTitle>
            <DialogDescription>
              Adicione um novo servidor WPPConnect ou Evolution API
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-nome">Nome do Servidor</Label>
              <Input
                id="create-nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="wpp-service-d"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-url">URL do Servidor</Label>
              <Input
                id="create-url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://wpp-service.up.railway.app"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-token">API Token</Label>
              <Input
                id="create-token"
                type="password"
                value={formData.api_token}
                onChange={(e) => setFormData({ ...formData, api_token: e.target.value })}
                placeholder="Token de autenticação"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-max">Máx. Sessões</Label>
                <Input
                  id="create-max"
                  type="number"
                  min={1}
                  max={10}
                  value={formData.max_sessions}
                  onChange={(e) => setFormData({ ...formData, max_sessions: parseInt(e.target.value) || 3 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-region">Região</Label>
                <Input
                  id="create-region"
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  placeholder="south-america"
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="create-shared">Servidor Compartilhado</Label>
                <p className="text-sm text-muted-foreground">
                  Usar como número central da empresa
                </p>
              </div>
              <Switch
                id="create-shared"
                checked={formData.is_shared}
                onCheckedChange={(checked) => setFormData({ ...formData, is_shared: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={isCreating || !formData.nome || !formData.url || !formData.api_token}
            >
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Criar Servidor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Servidor?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Todas as sessões vinculadas a este servidor perderão a referência.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
